import logger from './logger';


export const setSession = (user) => {
  if (!user || typeof user !== 'object') {
    logger.warn('Invalid user passed to setSession:', user);
    return;
  }

  const normalized = { ...user };

  // Normalize token field to user.token if missing
  if (!normalized.token) {
    const candidate = normalized.access_token || normalized.accessToken || normalized.authToken || normalized.bearer_token || normalized?.user?.token;
    if (candidate) {
      normalized.token = candidate;
    }
  }

  // Normalize refresh token
  if (normalized.refreshToken && !normalized.refresh_token) {
    normalized.refresh_token = normalized.refreshToken;
  }

  // Track when this token was issued/updated for proactive refresh logic
  if (!normalized.token_issued_at) {
    normalized.token_issued_at = new Date().toISOString();
  }

  localStorage.setItem('user', JSON.stringify(normalized));

  // Store token separately for backend compatibility
  if (normalized.token) {
    localStorage.setItem('authToken', normalized.token);
  }

  // Persist refresh token for backend refresh flow
  if (normalized.refresh_token) {
    localStorage.setItem('refresh_token', normalized.refresh_token);
  }
  try {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('sessionChanged', { detail: { user: normalized } }));
    }
  } catch (e) {
    // ignore
  }
};


export const getSession = () => {
  try {
    // Use only localStorage
    const data = localStorage.getItem('user');
    return data ? JSON.parse(data) : null;
  } catch (error) {
    logger.error('Error getting session:', error);
    return null;
  }
};

export const clearSession = () => {
  // Clear user session data
  localStorage.removeItem('user');
  localStorage.removeItem('userData');
  // Clear cached GMB profiles to prevent stale data after account switch
  localStorage.removeItem('availableGMBProfiles');
  localStorage.removeItem('availableGMBProfilesOwner');
  
  // Clear all user-specific caches (monthly plans, default profiles, etc.)
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('monthly_plan_') ||
        key.startsWith('weekly_products_services_') ||
        key.startsWith('visibeen_default_profile') ||
        key.startsWith('visibeen_tasks_') ||
        key.startsWith('visibeen_skip_product_service_')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      logger.log(`[clearSession] Cleared ${keysToRemove.length} user-specific cache entries`);
    }
  } catch (e) {
    logger.warn('[clearSession] Failed to clear user caches:', e);
  }
  
  // Clear selected business/profile data used by ProfilePlanContext
  localStorage.removeItem('selectedBusiness');
  localStorage.removeItem('selectedProfile');
  localStorage.removeItem('selectedGMBAccount');
  localStorage.removeItem('selectedGMBLocation');
  
  // Clear EDMS cached data
  localStorage.removeItem('accountInfo');
  localStorage.removeItem('accountInfoOwner');
  localStorage.removeItem('uploadedLogo');
  localStorage.removeItem('isLogoUploaded');
  localStorage.removeItem('selectedFontStyle');
  localStorage.removeItem('visibeen.selectedFooterId');

  // Clear backend/database tokens
  localStorage.removeItem('authToken');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('token');

  // Clear Google tokens
  localStorage.removeItem('googleAccessToken');
  localStorage.removeItem('google_token');
  localStorage.removeItem('googleRefreshToken');

  // Clear any other potential token storage
  localStorage.removeItem('bearer_token');
  localStorage.removeItem('id_token');

  // Clear URL parameters if possible
  if (typeof window !== 'undefined') {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('access_token');
      url.searchParams.delete('google_token');
      url.searchParams.delete('token');
      url.searchParams.delete('id_token');
      window.history.replaceState({}, document.title, url.pathname);
    } catch (e) {
      logger.warn('Could not clear URL parameters:', e);
    }
  }

  logger.log('Session and all tokens cleared successfully');
  try {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('sessionChanged', { detail: { user: null } }));
    }
  } catch (e) {}
};

// Determine if the session's token is expired based on expires_at (unix seconds or ISO)
export function isTokenExpired(session, skewSeconds = 60) {
  if (!session) return false;
  const expiresAt = session.expires_at || session.expiresAt;
  if (!expiresAt) return false;
  let expiryEpoch;
  if (typeof expiresAt === 'number') {
    expiryEpoch = expiresAt;
  } else {
    const parsed = Date.parse(expiresAt);
    if (Number.isNaN(parsed)) return false;
    expiryEpoch = Math.floor(parsed / 1000);
  }
  const now = Math.floor(Date.now() / 1000);
  return now >= (expiryEpoch - skewSeconds);
}

/**
 * Get the effective user ID for data fetching.
 * When admin is viewing a client's profile, returns the client's user ID.
 * Otherwise, returns the logged-in user's ID.
 *
 * @returns {{ userId: number|null, isAdminView: boolean, targetUserEmail: string|null }}
 */
export function getEffectiveUserId() {
  const session = getSession();
  let userId = session?.id ? parseInt(session.id) : null;
  let isAdminView = false;
  let targetUserEmail = null;

  try {
    const adminAccessData = sessionStorage.getItem('adminAccessData');
    if (adminAccessData) {
      const accessInfo = JSON.parse(adminAccessData);
      if (accessInfo?.targetUserId) {
        userId = parseInt(accessInfo.targetUserId);
        isAdminView = true;
        targetUserEmail = accessInfo.targetUserEmail || null;
        logger.log('[getEffectiveUserId] Admin viewing mode - using client user ID:', userId);
      }
    }
  } catch (error) {
    logger.warn('[getEffectiveUserId] Error checking admin access data:', error);
  }

  return { userId, isAdminView, targetUserEmail };
}

// Auto token extraction with multiple fallbacks
export function getAutoToken() {
  const session = getSession();

  if (!session) {
    // Fallback to localStorage tokens
    return (typeof window !== 'undefined')
      ? localStorage.getItem('authToken') || localStorage.getItem('access_token')
      : null;
  }

  // Try multiple token field names
  const tokenFields = [
    'token',
    'access_token',
    'accessToken',
    'authToken',
    'bearer_token'
  ];

  // Direct session level
  for (const field of tokenFields) {
    if (session[field]) return session[field];
  }

  // Nested in user object
  if (session.user) {
    for (const field of tokenFields) {
      if (session.user[field]) return session.user[field];
    }
  }

  // Nested in data object
  if (session.data) {
    for (const field of tokenFields) {
      if (session.data[field]) return session.data[field];
    }
  }

  // Final fallback to localStorage
  return (typeof window !== 'undefined')
    ? localStorage.getItem('authToken') || localStorage.getItem('access_token')
    : null;
}

// Auto setup session with token normalization
export function setAutoSession(authResponse) {
  if (!authResponse || typeof authResponse !== 'object') {
    logger.warn('Invalid auth response passed to setAutoSession:', authResponse);
    return null;
  }

  let session = { ...authResponse };

  // Normalize token field names
  if (authResponse.access_token && !authResponse.token) {
    session.token = authResponse.access_token;
  }
  if (authResponse.accessToken && !authResponse.token) {
    session.token = authResponse.accessToken;
  }
  if (authResponse.authToken && !authResponse.token) {
    session.token = authResponse.authToken;
  }

  // Set expiry if not present but expires_in is available
  if (authResponse.expires_in && !authResponse.expires_at) {
    session.expires_at = new Date(Date.now() + (authResponse.expires_in * 1000)).toISOString();
  }

  // Normalize refresh token
  if (authResponse.refreshToken && !authResponse.refresh_token) {
    session.refresh_token = authResponse.refreshToken;
  }
  if (authResponse.refresh_token && !session.refresh_token) {
    session.refresh_token = authResponse.refresh_token;
  }

  // Track token issue time
  session.token_issued_at = new Date().toISOString();

  logger.log('🔐 Setting auto session with normalized tokens');
  setSession(session);
  return session;
}

// Session validation utility
export function isSessionValid() {
  const session = getSession();
  const token = getAutoToken();

  return {
    hasSession: !!session,
    hasToken: !!token,
    hasRefreshToken: !!(session?.refresh_token || session?.refreshToken),
    isValid: !!(session && token)
  };
}

// Debug session information
export function getSessionDebugInfo() {
  const session = getSession();
  const autoToken = getAutoToken();
  const validation = isSessionValid();

  return {
    session,
    autoToken: autoToken ? `${autoToken.substring(0, 10)}...` : 'No token',
    validation,
    localStorage: {
      authToken: typeof window !== 'undefined' && localStorage.getItem('authToken')
        ? `${localStorage.getItem('authToken').substring(0, 10)}...` : 'No token',
      accessToken: typeof window !== 'undefined' && localStorage.getItem('access_token')
        ? `${localStorage.getItem('access_token').substring(0, 10)}...` : 'No token'
    }
  };
}

/**
 * Extract location ID from various formats:
 * - "accounts/xxx/locations/yyy" -> "yyy"
 * - "xxx:yyy" -> "yyy"
 * - "locations/yyy" -> "yyy"
 * - "yyy" -> "yyy" (already just the ID)
 * 
 * This helper is used for plan gating to match tier table location_id format
 */
export function extractLocationId(input) {
  if (!input) return null;

  const str = String(input);

  // Format: accounts/xxx/locations/yyy
  if (str.includes('/locations/')) {
    const match = str.match(/\/locations\/([^/]+)/);
    if (match) return match[1];
  }

  // Format: locations/yyy (without account prefix)
  if (str.startsWith('locations/')) {
    return str.replace('locations/', '');
  }

  // Format: accountId:locationId
  if (str.includes(':')) {
    const parts = str.split(':');
    if (parts.length === 2) return parts[1];
  }

  // Already just the location ID
  return str;
}

/**
 * Get location ID from selected business in localStorage
 * Returns the cleaned location ID suitable for tier table lookups
 */
export function getSelectedBusinessLocationId() {
  try {
    const selectedBusiness = localStorage.getItem('selectedBusiness');
    if (!selectedBusiness) return null;

    const business = JSON.parse(selectedBusiness);

    // Try various fields where location ID might be stored
    const rawId = business.name || business.location || business.location_id || business.locationId || business.id;

    return extractLocationId(rawId);
  } catch (error) {
    console.error('[authUtils] Error getting selected business location ID:', error);
    return null;
  }
}
